import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ChangePlanParams,
  Subscription,
  SubscriptionForm,
  SubscriptionStatus,
  UpgradeTo,
} from 'api/billing/types';
import { queryClient } from 'queries';
import { ACCOUNT_META_DATA_QUERY_KEY } from 'queries/account';
import {
  SUBSCRIPTION_KEY,
  useAddLicensesMutation,
  useChangePlanMutation,
  useForceSyncSubscriptionMutation,
  usePlanName,
} from 'queries/billing';
import { BillingCheckout } from './checkout';
import {
  EstimateModal,
  PaymentFailureModal,
  PaymentProgressModal,
  PaymentSuccessModal,
} from './modals';
import { BillingSelfServePortal } from './portal';
import { getPlanName } from './utils';

interface Props {
  subscription: Subscription;
  upgradeTo: UpgradeTo;
  onDismiss: () => void;
}

export const BillingUpgrade = ({
  subscription,
  upgradeTo,
  onDismiss,
}: Props) => {
  const {
    subscriptionMeta: { paymentMethodActive },
  } = subscription;
  const upgradeToPlanName = usePlanName(upgradeTo.plan);

  const isUpgradingLicenses: boolean = useMemo(
    () =>
      Object.keys(upgradeTo).includes('licenseCount') &&
      Object.keys(upgradeTo).length === 1 &&
      subscription.subscriptionStatus === SubscriptionStatus.ACTIVE,
    [subscription.subscriptionStatus, upgradeTo],
  );

  const [loadCheckout, setLoadCheckout] = useState(false);
  const [loadPortal, setLoadPortal] = useState(false);
  const [showPaymentStatus, setShowPaymentStatus] = useState(false);
  const changePlanParams = useRef<ChangePlanParams>();
  const {
    mutateAsync: changePlan,
    isLoading,
    isSuccess,
    isError,
  } = useChangePlanMutation();
  const {
    mutateAsync: addLicenses,
    isLoading: isAddingLicenses,
    isSuccess: isAddLicenseSuccess,
    isError: isAddLicenseError,
  } = useAddLicensesMutation();
  const { mutateAsync: forceSyncSubscription } =
    useForceSyncSubscriptionMutation();

  const onUpgradeSuccess = useCallback(async () => {
    await forceSyncSubscription(subscription.subscriptionId);
    await queryClient.invalidateQueries(SUBSCRIPTION_KEY);
    await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
  }, [forceSyncSubscription, subscription.subscriptionId]);

  const onConfirm = useCallback(
    async (formValues: SubscriptionForm) => {
      changePlanParams.current = {
        form: formValues,
        plan: upgradeTo.plan || getPlanName(subscription.plan.planName),
        subscription,
      };
      if (paymentMethodActive) {
        setShowPaymentStatus(true);
        if (isUpgradingLicenses) {
          await addLicenses(formValues.quantity);
        } else {
          await changePlan(changePlanParams.current);
        }
        onUpgradeSuccess();
      } else {
        if (isUpgradingLicenses) {
          changePlanParams.current = {
            ...changePlanParams.current,
            form: {
              ...(formValues || {}),
              quantity: subscription.licenseQuantity + formValues.quantity,
            },
          };
        }
        setLoadCheckout(true);
      }
    },
    [
      upgradeTo.plan,
      subscription,
      paymentMethodActive,
      isUpgradingLicenses,
      onUpgradeSuccess,
      addLicenses,
      changePlan,
    ],
  );

  const onUpdateCard = useCallback(() => {
    setShowPaymentStatus(false);
    setLoadPortal(true);
  }, []);

  return (
    <>
      {!(showPaymentStatus || loadCheckout || loadPortal) && (
        <EstimateModal
          upgradeTo={upgradeTo}
          subscription={subscription}
          onConfirm={onConfirm}
          onCancel={onDismiss}
        />
      )}
      {showPaymentStatus && (
        <>
          {(isLoading || isAddingLicenses) && <PaymentProgressModal />}
          {(isSuccess || isAddLicenseSuccess) && (
            <PaymentSuccessModal
              message={
                isUpgradingLicenses ? (
                  <FormattedMessage
                    id="billing.upgrade.license.success.message"
                    values={{
                      licenseCount: changePlanParams.current?.form?.quantity,
                    }}
                  />
                ) : upgradeTo.plan ? (
                  <FormattedMessage
                    id="billing.upgrade.plan.success.message"
                    values={{
                      plan: upgradeToPlanName,
                    }}
                  />
                ) : (
                  <FormattedMessage id="billing.upgrade.cycle.success.message" />
                )
              }
              onOk={onDismiss}
              onCancel={onDismiss}
            />
          )}
          {(isError || isAddLicenseError) && (
            <PaymentFailureModal onOk={onUpdateCard} onCancel={onDismiss} />
          )}
        </>
      )}
      {loadCheckout && changePlanParams.current && (
        <BillingCheckout
          changePlanParams={changePlanParams.current}
          onSuccess={onUpgradeSuccess}
          onDismiss={onDismiss}
        />
      )}
      {loadPortal && <BillingSelfServePortal onDismiss={onDismiss} />}
    </>
  );
};
